import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import srOnly from 'styles/tools/sr-only'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Clickable from 'components/Common/Clickable'
import MapViewBand from 'components/Common/MapViewBand'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { buildUrl } from 'lib/url/searchUrlUtils'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import ExperienceDetailView from './ExperienceDetailView'
import ExperienceSearchResults from './ExperienceSearchResults'
import type { DrawerExperienceSearchTile } from './ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './mapExperienceDetailToDrawer'
import { formatExperienceHeaderSubtitle } from './formatExperienceHeaderSubtitle'
import useExperienceDetail from '../../hooks/useExperienceDetail'
import useExperienceDetails from '../../hooks/useExperienceDetails'
import useOfferReviews from '../../hooks/useOfferReviews'
import useExperienceSearch from './useExperienceSearch'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { useExperiences } from 'tripPlanner/hooks/api/offer'
import uuidV4 from 'lib/string/uuidV4Utils'
import useTripPlannerOfferListTracking from 'tripPlanner/hooks/useTripPlannerOfferListTracking'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { getSavedExperienceOfferIds } from 'tripPlanner/utils/chatItinerary'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'
import { useOfferDrawerOpenedDispatch } from '../../hooks/useOfferDrawerOpenedDispatch'
import { arrayToMap, partitionBy } from 'lib/array/arrayUtils'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from '../TripDrawerAssistantBridge'
import ListingDrawerSearchControls from '../ListingDrawerSearchControls'
import useExperienceListingSearchState, { EXPERIENCE_DRAWER_SORT } from './useExperienceListingSearchState'
import { compactLines } from '../../utils/drawerAskAIContext'
import { formatTripDrawerMapLabel } from '../formatTripDrawerMapLabel'
import { DrawerBodyContentWrapper } from '../drawerBodyLayout.styles'
import {
  LISTING_CONTENT_INSET_DESKTOP,
  LISTING_CONTENT_MAX_WIDTH,
  listingContentContainer,
} from './constants'
import ThingsToDoPlacesTab from '../ThingsToDo/ThingsToDoPlacesTab'
import { THINGS_TO_DO_HEADING } from '../ThingsToDo/thingsToDoMessages'
import useLowestExperiencePrice from '../../hooks/useLowestExperiencePrice'
import ExperienceCategoryFilters from './ExperienceCategoryFilters'
import { filterExperiencesByCategory, getExperienceCategoryFilters, resolveEffectiveCategoryId } from './experienceCategoryFiltering'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildPlaceDrawerOpenedActivityEntry } from '../../containers/buildPlaceDrawerOpenedActivityEntry'
import type { ChatExploreCategoryKey } from 'tripPlanner/config/exploreCategories'
import type { ExploreItem } from 'tripPlanner/types/common'

const MAX_EXPERIENCE_SEARCH_RESULTS_IN_CONTEXT = 3

// Vertical then horizontal, per Sheet's padding shorthand.
const LISTING_PADDING = '24 20' as const
const LISTING_PADDING_DESKTOP = `24 ${LISTING_CONTENT_INSET_DESKTOP}` as const

/* A region that is only mounted when it already holds its text is not reliably announced. */
const LiveRegion = styled.div`
  ${srOnly}
`

const ListingContentSheet = styled(Sheet)`
  ${listingContentContainer}
  max-width: ${rem(LISTING_CONTENT_MAX_WIDTH)};
  margin-inline: auto;
`

const EXPERIENCES_MAP_CATEGORY_KEY: ChatExploreCategoryKey = 'experiences'

function getExperienceSearchStatus(
  { isLoading, isError, hasExperiences }: { isLoading: boolean; isError: boolean; hasExperiences: boolean },
): string {
  if (isLoading) return 'searching'
  if (isError) return 'error'
  return hasExperiences ? 'searched' : 'no results'
}

const PLACES_TAB = 'places'
const EXPERIENCES_TAB = 'experiences'
type DrawerListTab = typeof PLACES_TAB | typeof EXPERIENCES_TAB

const ContentWrapper = DrawerBodyContentWrapper

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

/*
 * LuxKit sizes each segment from its own label and both distributions floor a column at its own
 * max-content, so an even split has to be imposed: `1fr` tracks shrink to the widest label.
 */
const ThingsToDoTabs = styled(SegmentedOptionsGroup)`
  /* Doubled up to clear the two-class distribution rule this overrides; the fieldset's only
     child is the track container, whichever distribution the group was given. */
  && > fieldset > div {
    grid-auto-columns: 1fr;
  }
`

function formatExperienceSearchResult(
  experience: DrawerExperienceSearchTile,
  index: number,
  currencyCode: string,
): string {
  const price = experience.price !== undefined
    ? `, from ${currencyCode} ${experience.price}`
    : ''
  return `Result ${index + 1}: ${experience.name}${price}`
}

export interface ExperienceListingDrawerProps {
  isOpen: boolean
  onClose: () => void
  placeName: string
  /** Absent when the drawer is opened for a bare place (e.g. a breadcrumb). */
  placeCountry?: string
  /** LE place id for `placeName`, when the opener resolved one. */
  placeId?: string
  /** Seed the search from `placeName` rather than from the trip's recent
   *  searches. Every stop-less drawer shares the one `tripStopId: null` recent
   *  search, so without this the second place opened re-runs the first's
   *  search instead of its own. */
  seedSearchFromPlace?: boolean
  /** Back out of a drawer stacked on top of another one. The drawer's own
   *  list <-> detail back takes priority while it has somewhere to return to. */
  onBack?: () => void
  onAfterClose?: () => void
  animation?: TripDrawerAnimation
  stopCoordinates?: { lat: number; lng: number }
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onShowExperienceOnMap?: (experience: {
    offerId: string
    name: string
    geoLocation: { lat: number; lng: number }
  }) => void
  contextStop?: TripStop
  initialSelectedOfferId?: string
}

function ExperienceListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  placeId,
  seedSearchFromPlace,
  stopCoordinates,
  onBack,
  onAfterClose,
  animation,
  itineraryData,
  onSendMessage,
  onShowExperienceOnMap,
  contextStop,
  initialSelectedOfferId,
}: ExperienceListingDrawerProps) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const eventBus = useTripPlannerEventBusOptional()
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)
  // True only when the detail view was reached by selecting an experience from the
  // listing, so "back" has a listing to return to. Opened straight into detail
  // (initialSelectedOfferId, e.g. from the timeline) shows a close (X) instead.
  const [detailFromListing, setDetailFromListing] = useState(false)
  const [selectedExperienceName, setSelectedExperienceName] = useState<string | undefined>(
    undefined,
  )
  const [drawerSessionKey, setDrawerSessionKey] = useState(() => uuidV4())
  /* Lazily from capability, not unconditionally: with no coordinates the Places tab never renders,
     so starting there would silently move the traveller off Experiences once they resolve. */
  const [activeTab, setActiveTab] = useState<DrawerListTab>(
    () => (stopCoordinates ? PLACES_TAB : EXPERIENCES_TAB),
  )
  const [selectedExperienceCategoryId, setSelectedExperienceCategoryId] = useState<number | undefined>(undefined)
  const canShowPlaces = !!stopCoordinates
  const showPlacesTab = canShowPlaces && activeTab === PLACES_TAB
  const showExperiencesTab = !canShowPlaces || activeTab === EXPERIENCES_TAB
  const isDetailView = !!selectedOfferId
  const handleOpenReset = useCallback(() => {
    setDrawerSessionKey(uuidV4())
    setSelectedOfferId(initialSelectedOfferId)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
    setSelectedExperienceCategoryId(undefined)
    setActiveTab(stopCoordinates ? PLACES_TAB : EXPERIENCES_TAB)
  }, [initialSelectedOfferId, stopCoordinates])
  const {
    browseAllResultsUrl,
    fallbackPlaceIds,
    handleDateChange,
    handleSearch,
    handleSearchSubmit,
    isSearchSessionReady,
    isLocationOrDateRefined,
    isRefinedSearch,
    resolvedDestination,
    resolvedExperienceSearchPlace,
    resolvedPlaceId,
    searchLocation,
    searchQuery,
    selectedDateRange,
    tripDateRange,
  } = useExperienceListingSearchState({
    isOpen,
    placeName,
    placeCountry,
    placeId,
    seedSearchFromPlace,
    stopCoordinates,
    contextStop,
    itineraryData,
    regionCode: geo.currentRegionCode,
    onOpenReset: handleOpenReset,
  })

  const searchCalendarFocusDate = useMemo(() => {
    const resolved = resolveSearchDefaults({
      surface: 'experiences',
      stopId: contextStop?.id ?? null,
      recentSearches: itineraryData?.recentSearches,
      indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
      selected: { dateRange: selectedDateRange, sort: EXPERIENCE_DRAWER_SORT },
      concrete: {
        checkIn: contextStop?.startDate ?? contextStop?.days.find(day => day.date)?.date,
        checkOut: contextStop?.endDate,
        locationQuery: [placeName, placeCountry].filter(Boolean).join(', '),
      },
    })

    return resolved.surface === 'experiences' ? resolved.focusMonth : new Date()
  }, [
    contextStop,
    itineraryData?.indicativeSearchMonth,
    itineraryData?.recentSearches,
    placeCountry,
    placeName,
    selectedDateRange,
  ])

  const { experiences, searchContext, isLoading, isError, refetch } = useExperienceSearch({
    searchQuery,
    placeId: resolvedPlaceId,
    fallbackPlace: resolvedExperienceSearchPlace,
    fallbackPlaceIds,
    from: selectedDateRange.checkIn,
    to: selectedDateRange.checkOut,
    sortBy: EXPERIENCE_DRAWER_SORT,
    enabled: isSearchSessionReady,
  })
  const experienceOfferIds = useMemo(
    () => experiences.map((experience) => experience.offerId),
    [experiences],
  )
  // Only the experience tiles read these, and the detail view renders no list.
  const trackingOffers = useExperiences({
    currentCurrency: currency,
    experienceIds: isOpen && showExperiencesTab && !isDetailView ? experienceOfferIds : [],
    currentRegionCode: geo.currentRegionCode,
  })
  const trackingOfferById = useMemo(() => {
    return arrayToMap(trackingOffers.data, (offer) => offer.id)
  }, [trackingOffers.data])
  const getTrackingOffer = useCallback(
    (offerId: string) => {
      return trackingOfferById.get(offerId)
    },
    [trackingOfferById],
  )

  const {
    experienceDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch: refetchDetail,
  } = useExperienceDetail({
    offerId: selectedOfferId,
    currencyCode: currency,
    enabled: !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!experienceDetail,
  })

  const detailExperience = useMemo(() => {
    if (!experienceDetail) return undefined
    return mapExperienceDetailToDrawer(experienceDetail)
  }, [experienceDetail])

  // See useOfferDrawerOpenedDispatch for the dedup rationale (also shared with HotelDetailView).
  useOfferDrawerOpenedDispatch(selectedOfferId, detailExperience?.name, 'experience')

  const defaultListHeaderTitle = intl.formatMessage(
    {
      id: 'V6Sb0Bka9c4FK9WOArYJ',
      defaultMessage: 'Experiences in {placeName}',
      description:
        'Heading for the experience listing drawer showing available experiences for a specific stop location.',
    },
    { placeName },
  )
  const refinedListHeaderTitle = intl.formatMessage({
    id: 'T8oIelE1tY_weHdZkoSC',
    defaultMessage: 'Search experiences',
    description:
      'Generic title for experience listing drawer after users refine the drawer search.',
  })
  const placesListHeaderTitle = intl.formatMessage(THINGS_TO_DO_HEADING, { placeName })
  const experiencesListHeaderTitle = isRefinedSearch ?
    refinedListHeaderTitle :
    defaultListHeaderTitle

  const destinationQueryText =
    contextStop?.stopPlaceDetail?.formattedAddress ?? placeName
  const searchFailedMessage = intl.formatMessage({
    id: '_prz987A-9P1umJt9l9D',
    defaultMessage: 'Failed to load experiences.',
    description: 'Error message shown when experience search results fail to load in the drawer.',
  })

  const listHeaderTitle = showPlacesTab
    ? placesListHeaderTitle
    : experiencesListHeaderTitle
  const shouldRenderTitleInContent =
    shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const showDetailTitleInContent =
    isDetailView && shouldRenderTitleInContent
  const showListTitleInContent =
    !isDetailView && shouldRenderTitleInContent

  const drawerHeader = useMemo(() => {
    if (showDetailTitleInContent || showListTitleInContent) return undefined

    const title = selectedOfferId
      ? detailExperience?.name ?? selectedExperienceName
      : listHeaderTitle
    if (!title) return undefined

    const subtitle =
      selectedOfferId && detailExperience
        ? formatExperienceHeaderSubtitle(detailExperience)
        : undefined

    return <TripDrawerHeaderTitle title={title} subtitle={subtitle} />
  }, [
    detailExperience,
    listHeaderTitle,
    selectedExperienceName,
    selectedOfferId,
    showDetailTitleInContent,
    showListTitleInContent,
  ])

  const handleSelectExperience = useCallback(
    (experience: DrawerExperienceSearchTile) => {
      setSelectedOfferId(experience.offerId)
      setDetailFromListing(true)
      setSelectedExperienceName(experience.name)
    },
    [],
  )

  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setDetailFromListing(false)
    setSelectedExperienceName(undefined)
  }, [])

  const handleViewExperiences = useCallback(() => {
    setActiveTab(EXPERIENCES_TAB)
  }, [])

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value === PLACES_TAB ? PLACES_TAB : EXPERIENCES_TAB)
  }, [])

  const handleSelectPlace = useCallback((place: ExploreItem) => {
    eventBus?.dispatch({
      type: 'open_place_drawer',
      googlePlaceId: place.id,
      placeName: place.name,
    })
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(place.name, 'trip_planner.drawer.things_to_do_tile', place.id)],
    })
  }, [eventBus])

  const handleViewPlacesOnMap = useCallback((categoryKey: ChatExploreCategoryKey) => {
    if (!stopCoordinates) return
    handleClose()
    eventBus?.dispatch({
      type: 'show_explore_on_map',
      coordinates: stopCoordinates,
      categoryKey,
      destinationQueryText,
      returnToListingSearch: {
        vertical: 'experience',
        placeName,
        placeCountry,
        placeId,
        stopId: contextStop?.id,
      },
    })
  }, [
    contextStop?.id,
    destinationQueryText,
    eventBus,
    handleClose,
    placeCountry,
    placeId,
    placeName,
    stopCoordinates,
  ])

  const handleViewExperiencesOnMap = useCallback(() => {
    handleViewPlacesOnMap(EXPERIENCES_MAP_CATEGORY_KEY)
  }, [handleViewPlacesOnMap])

  // The drawer's own reset first, then the owner's -- a stacked drawer's owner
  // pops it off the stack here, which unmounts everything below.
  const handleDrawerAfterClose = useCallback(() => {
    handleAfterClose()
    onAfterClose?.()
  }, [handleAfterClose, onAfterClose])

  const handleRetryDetail = useCallback(() => {
    refetchDetail()
  }, [refetchDetail])

  const handleRetrySearch = useCallback(() => {
    refetch()
  }, [refetch])

  const canGoBack = isDetailView && detailFromListing
  const savedExperienceStop = isLocationOrDateRefined ? undefined : contextStop
  const savedExperienceOfferIds = useMemo(() => {
    return getSavedExperienceOfferIds(savedExperienceStop)
  }, [savedExperienceStop])
  const hasSavedExperiences = savedExperienceOfferIds.size > 0

  // Split the live results into saved vs explore. A saved experience that is still
  // in the results keeps its rich search tile.
  const [savedSearchExperiences, exploreExperiences] = useMemo(() => {
    if (!hasSavedExperiences) return [[], experiences]
    return partitionBy(experiences, (experience) =>
      savedExperienceOfferIds.has(experience.offerId),
    )
  }, [experiences, hasSavedExperiences, savedExperienceOfferIds])

  const experienceCategoryFilters = useMemo(
    () => getExperienceCategoryFilters(exploreExperiences),
    [exploreExperiences],
  )

  const effectiveCategoryId = useMemo(
    () => resolveEffectiveCategoryId(experienceCategoryFilters, selectedExperienceCategoryId),
    [experienceCategoryFilters, selectedExperienceCategoryId],
  )

  const sortedExploreExperiences = useMemo(
    () => filterExperiencesByCategory(exploreExperiences, effectiveCategoryId),
    [effectiveCategoryId, exploreExperiences],
  )

  // Saved experiences are counted from itinerary state, so any saved offer that the
  // live search omits must be backfilled from its offer detail — otherwise the stop
  // tile shows "{n} saved" while the drawer's Saved section silently drops rows.
  const missingSavedOfferIds = useMemo(() => {
    const presentOfferIds = new Set(
      savedSearchExperiences.map((experience) => experience.offerId),
    )
    return Array.from(savedExperienceOfferIds).filter(
      (offerId) => !presentOfferIds.has(offerId),
    )
  }, [savedExperienceOfferIds, savedSearchExperiences])
  // Not gated on the tab: the assistant's saved-results fact is reported from either one.
  const { experienceDetails: missingSavedDetails } = useExperienceDetails({
    offerIds: missingSavedOfferIds,
    currencyCode: currency,
    enabled: isOpen,
  })
  const savedExperiences = useMemo(() => {
    return [
      ...savedSearchExperiences,
      ...missingSavedDetails.map(mapExperienceDetailToDrawer),
    ]
  }, [savedSearchExperiences, missingSavedDetails])
  const hasExperiences = experiences.length > 0 || savedExperiences.length > 0

  const { formatted: formattedLowestExperiencePrice } = useLowestExperiencePrice(experiences)

  // Undefined while settling or empty: the count is the whole reason the row is there.
  const experiencesSummary = useMemo(() => {
    if (isLoading || isError || experiences.length === 0) return undefined

    return { count: experiences.length, formattedLowestPrice: formattedLowestExperiencePrice }
  }, [experiences.length, formattedLowestExperiencePrice, isError, isLoading])
  const savedHeading = intl.formatMessage({
    id: 'Kqe4G7DQZXoc84Lv-JBS',
    defaultMessage: 'Saved',
    description:
      'Section heading for saved experiences in the trip planner experience drawer',
  })
  const exploreHeading = intl.formatMessage({
    id: 'qiheP9joj-3erCzoLW6p',
    defaultMessage: 'Explore more',
    description:
      'Section heading above experience search results in the trip planner experience drawer',
  })
  const searchedPlaceName = searchContext?.searchedPlace?.name
  const searchedPlaceId = searchContext?.searchedPlace?.placeId
  const requestedPlaceName = searchContext?.requestedPlace?.name ?? placeName
  const finalBrowseAllResultsUrl = useMemo(() => {
    if (!searchContext?.isFallbackPlace || !searchedPlaceId || !searchedPlaceName) {
      return browseAllResultsUrl
    }

    return buildUrl(
      `/search/experiences?${replaceExperienceQueryString({
        destinationId: searchedPlaceId,
        destinationName: searchedPlaceName,
        from: selectedDateRange.checkIn,
        to: selectedDateRange.checkOut,
      })}`,
      geo.currentRegionCode,
    )
  }, [
    browseAllResultsUrl,
    geo.currentRegionCode,
    searchContext?.isFallbackPlace,
    searchedPlaceId,
    searchedPlaceName,
    selectedDateRange.checkIn,
    selectedDateRange.checkOut,
  ])
  const fallbackNotice = useMemo(() => {
    if (!searchContext?.isFallbackPlace || !searchedPlaceName) {
      return undefined
    }

    return intl.formatMessage(
      {
        id: '9X1qqVfL80vT5TpM8qxN',
        defaultMessage: 'No experiences in {requestedPlaceName} — showing the nearest',
        description:
          'Notice shown when the trip planner experience drawer shows broader destination results after the selected stop has no experience results.',
      },
      { requestedPlaceName },
    )
  }, [
    intl,
    requestedPlaceName,
    searchContext?.isFallbackPlace,
    searchedPlaceName,
  ])

  const handleShowMapFromHeader = useCallback(() => {
    if (detailExperience?.geoLocation) {
      onShowExperienceOnMap?.({
        offerId: detailExperience.offerId,
        name: detailExperience.name,
        geoLocation: detailExperience.geoLocation,
      })
    }
  }, [detailExperience, onShowExperienceOnMap])

  const headerActions = useMemo(() => {
    if (!isDetailView || !detailExperience) return undefined

    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {detailExperience.geoLocation && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMapFromHeader}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {itineraryData && onSendMessage && (
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={detailExperience.name}
            offerId={detailExperience.offerId}
            offerType="experience"
            coordinates={detailExperience.geoLocation}
            variant="outline"
          />
        )}
      </Group>
    )
  }, [
    detailExperience,
    drawerPresentation,
    handleShowMapFromHeader,
    intl,
    isDetailView,
    itineraryData,
    onSendMessage,
  ])

  const tracking = useTripPlannerOfferListTracking(
    'experienceSearchDrawer',
    [
      drawerSessionKey,
      searchQuery,
      resolvedPlaceId,
      selectedDateRange.checkIn,
      selectedDateRange.checkOut,
    ].filter(Boolean).join(':'),
  )

  const shouldShowDetailLoadingTitle =
    showDetailTitleInContent && !!selectedExperienceName
  const assistantExperienceSearchDetails = !isDetailView
    ? compactLines([
        'Drawer: Experience search',
        `Status: ${getExperienceSearchStatus({ isLoading, isError, hasExperiences })}`,
        `Destination: ${searchQuery}`,
        selectedDateRange.checkIn && `From: ${selectedDateRange.checkIn}`,
        selectedDateRange.checkOut && `To: ${selectedDateRange.checkOut}`,
        fallbackNotice && `Search note: ${fallbackNotice}`,
        `Saved results: ${savedExperiences.length}`,
        `Explore results visible: ${sortedExploreExperiences.length}`,
        sortedExploreExperiences.length > 0 && 'Top visible results:',
        ...sortedExploreExperiences
          .slice(0, MAX_EXPERIENCE_SEARCH_RESULTS_IN_CONTEXT)
          .map((experience, index) => formatExperienceSearchResult(experience, index, currency)),
      ])
    : undefined
  const assistantContextLabel = isDetailView ? detailExperience?.name : listHeaderTitle
  const assistantContextDetails = isDetailView ? undefined : assistantExperienceSearchDetails

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleDrawerAfterClose}
      onBack={canGoBack ? handleBack : onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={isDetailView ? detailExperience?.name : undefined}
          isOpen={isOpen}
        />
      }
      headerActions={isDetailView ? headerActions : undefined}
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen}
        contextLabel={assistantContextLabel}
        contextDetails={assistantContextDetails}
      />
      {isDetailView && (
        <>
          {isDetailLoading && !detailExperience && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                {shouldShowDetailLoadingTitle && (
                  <Heading variant="heading3">{selectedExperienceName}</Heading>
                )}
                {!shouldShowDetailLoadingTitle && (
                  <LoadingBox height={28} width={240} borderRadius="S" />
                )}
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailExperience && (
            <ContentWrapper>
              <Group
                direction="vertical"
                gap={8}
                horizontalAlign="center"
                verticalAlign="center"
              >
                {showDetailTitleInContent && selectedExperienceName && (
                  <Heading variant="heading3">{selectedExperienceName}</Heading>
                )}
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'clAQJsTYp1RaYNVY1-Rz',
                    defaultMessage: 'Failed to load experience details.',
                    description:
                      'Error message shown when experience offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={handleRetryDetail}
                >
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description:
                      'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailExperience && (
            <ExperienceDetailView
              key={detailExperience.offerId}
              experience={detailExperience}
              onEnlargeMap={
                detailExperience.geoLocation && onShowExperienceOnMap
                  ? handleShowMapFromHeader
                  : undefined
              }
              standaloneOffer={trackingOfferById.get(detailExperience.offerId)}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              scrollToPricing={false}
              showTitleInContent={showDetailTitleInContent}
              showLocationInContent={showDetailTitleInContent}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ListingContentSheet padding={LISTING_PADDING} desktopPadding={LISTING_PADDING_DESKTOP}>
          <Group direction="vertical" gap={24}>
            {showListTitleInContent && (
              <Group direction="vertical" gap={4}>
                <Heading variant="heading3">{listHeaderTitle}</Heading>
                <BodyText variant="large">{searchLocation}</BodyText>
              </Group>
            )}

            {canShowPlaces && (
              <Group direction="horizontal" horizontalAlign="center">
                <ThingsToDoTabs
                  inputsName="tripChatThingsToDoTab"
                  onValueChange={handleTabChange}
                >
                  <SegmentedOption
                    value={PLACES_TAB}
                    checked={activeTab === PLACES_TAB}
                    title={intl.formatMessage({
                      id: 'MAHf5HdXtALFbUWZHclo',
                      defaultMessage: 'Places',
                      description: 'Tab label for the Google-sourced places list in the trip planner things to do drawer',
                    })}
                  />
                  <SegmentedOption
                    value={EXPERIENCES_TAB}
                    checked={activeTab === EXPERIENCES_TAB}
                    title={intl.formatMessage({
                      id: '3yUk0e2AU-j09XzBy2O8',
                      defaultMessage: 'Experiences',
                      description: 'Tab label for the bookable Luxury Escapes experiences list in the trip planner things to do drawer',
                    })}
                  />
                </ThingsToDoTabs>
              </Group>
            )}

            {showPlacesTab && (
              <ThingsToDoPlacesTab
                destinationName={placeName}
                destinationQueryText={destinationQueryText}
                coordinates={stopCoordinates}
                onSelectPlace={handleSelectPlace}
                onViewOnMap={eventBus ? handleViewPlacesOnMap : undefined}
                experiencesSummary={experiencesSummary}
                onViewExperiences={handleViewExperiences}
                itineraryData={itineraryData}
                onSendMessage={onSendMessage}
              />
            )}

            {showExperiencesTab && (
              <Group direction="vertical" gap={24}>
                <LiveRegion aria-live="polite" aria-atomic="true">
                  {isError && !isLoading ? searchFailedMessage : ''}
                </LiveRegion>
                <ListingDrawerSearchControls
                  searchLabel={intl.formatMessage({
                    id: 'to9wSYVjr1w5GayYemuZ',
                    defaultMessage: 'Destination',
                    description:
                      'Destination search input label in the trip planner experience drawer',
                  })}
                  searchValue={searchLocation}
                  searchItem={resolvedDestination}
                  searchVertical="experience"
                  searchPlaceholder={intl.formatMessage({
                    id: 'NCxoqvZemIIh28fGg6Ao',
                    defaultMessage: 'Search destinations',
                    description:
                      'Placeholder for destination search in the trip planner experience drawer',
                  })}
                  onSearch={handleSearch}
                  onSearchSubmit={handleSearchSubmit}
                  showDestinationField={false}
                  checkIn={selectedDateRange.checkIn}
                  checkOut={selectedDateRange.checkOut}
                  onDateChange={handleDateChange}
                  whenLabel={intl.formatMessage({
                    id: 'bR5cnLyI_fV2S_o1ZOev',
                    defaultMessage: 'When',
                    description:
                      'When date field label in the trip planner experience drawer',
                  })}
                  experienceTripDateRange={tripDateRange}
                  searchCalendarFocusDate={searchCalendarFocusDate}
                />

                {fallbackNotice && (
                  <BodyText variant="medium" weight="bold">
                    {fallbackNotice}
                  </BodyText>
                )}

                {stopCoordinates && eventBus && (
                  <Clickable onClick={handleViewExperiencesOnMap}>
                    <MapViewBand isNonInteractable />
                  </Clickable>
                )}

                {experienceCategoryFilters.length > 0 && (
                  <ExperienceCategoryFilters
                    categories={experienceCategoryFilters}
                    selectedCategoryId={effectiveCategoryId}
                    isFetching={isLoading}
                    onSelectCategory={setSelectedExperienceCategoryId}
                  />
                )}

                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16}>
                    {/* Saved comes from itinerary state, so a failed search must not take it down. */}
                    {savedExperiences.length > 0 && (
                      <Group direction="vertical" gap={12}>
                        <Heading variant="heading4">{savedHeading}</Heading>
                        <ExperienceSearchResults
                          experiences={savedExperiences}
                          isLoading={false}
                          onSelectExperience={handleSelectExperience}
                          placeName={placeName}
                          browseAllResultsUrl={finalBrowseAllResultsUrl}
                          itineraryData={itineraryData}
                          onSendMessage={onSendMessage}
                          getTrackingOffer={getTrackingOffer}
                          positionOffset={0}
                        />
                      </Group>
                    )}

                    {isError && !isLoading && (
                      <Group
                        direction="vertical"
                        gap={8}
                        horizontalAlign="center"
                        verticalAlign="center"
                      >
                        <BodyText variant="medium" weight="bold">
                          {searchFailedMessage}
                        </BodyText>
                        <TextButton
                          kind="secondary"
                          size="small"
                          onClick={handleRetrySearch}
                        >
                          {intl.formatMessage({
                            id: 'MUzm0X4vh_i2I0FMkGBi',
                            defaultMessage: 'Try again',
                            description:
                              'Button label to retry loading experience search results in the drawer.',
                          })}
                        </TextButton>
                      </Group>
                    )}

                    {!isError && (isLoading || !hasExperiences) && (
                      <ExperienceSearchResults
                        experiences={sortedExploreExperiences}
                        isLoading={isLoading}
                        onSelectExperience={handleSelectExperience}
                        placeName={placeName}
                        browseAllResultsUrl={finalBrowseAllResultsUrl}
                        itineraryData={itineraryData}
                        onSendMessage={onSendMessage}
                        getTrackingOffer={getTrackingOffer}
                      />
                    )}

                    {!isError && !isLoading && sortedExploreExperiences.length > 0 && (
                      <Group direction="vertical" gap={12}>
                        {savedExperiences.length > 0 && (
                          <Heading variant="heading4">{exploreHeading}</Heading>
                        )}
                        <ExperienceSearchResults
                          experiences={sortedExploreExperiences}
                          isLoading={false}
                          onSelectExperience={handleSelectExperience}
                          placeName={placeName}
                          browseAllResultsUrl={finalBrowseAllResultsUrl}
                          itineraryData={itineraryData}
                          onSendMessage={onSendMessage}
                          getTrackingOffer={getTrackingOffer}
                          positionOffset={savedExperiences.length}
                        />
                      </Group>
                    )}
                  </Group>
                </OfferListEventsProvider>
              </Group>
            )}
          </Group>
        </ListingContentSheet>
      )}
    </TripDrawer>
  )
}

export default ExperienceListingDrawer
